import {
  Container,
  Typography,
  Stepper,
  Step,
  Button,
  Box,
  Divider,
  Stack,
  StepButton,
} from "@mui/material";
import { MetaAdsTrackingTemplate } from "features/utms";
import {
  metaIAIAdditionalMapping,
  metaIAIMapping,
} from "features/utms/components/MetaAdsTrackingTemplate";
import {
  I_CAMPAIGNID,
  I_CONTENTID,
  I_MEDIUM,
  I_SOURCE,
  I_ADID,
} from "features/utms/constants";
import { useState } from "react";

const steps = [
  "Sign in to Meta Ads Manager",
  "Select Your Campaign",
  "Navigate to Ad Set",
  "Set Up Tracking Template",
  "Explanation of Parameters",
  "Save the Changes",
];

interface IMetaAdsTrackingSetupProps {
  onFinish: () => void;
}

function MetaAdsTrackingSetup({ onFinish }: IMetaAdsTrackingSetupProps) {
  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Typography>
            Log in to your Facebook (Meta) Ads Manager account at{" "}
            <a
              href="https://adsmanager.facebook.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              Meta Ads Manager
            </a>
            .
          </Typography>
        );
      case 1:
        return (
          <Typography>
            Select the campaign you want to manage from the list of your active
            campaigns.
          </Typography>
        );
      case 2:
        return (
          <Typography>
            In the selected campaign, navigate to the <strong>Ad</strong> you
            want to configure. Click the edit button (the pencil icon)
          </Typography>
        );
      case 3:
        return <MetaAdsTrackingTemplate />;
      case 4:
        return (
          <Box>
            <Typography>
              <strong>Explanation of Parameters:</strong>
            </Typography>
            <ul>
              <li>
                <strong>{I_SOURCE}</strong>: Represents the source of the
                traffic. Set this to <code>{metaIAIMapping[I_SOURCE]}</code>.
              </li>
              <li>
                <strong>{I_ADID}</strong>: Dynamically inserts the ad Id. Use{" "}
                <code>{metaIAIMapping[I_ADID]}</code>.
              </li>
            </ul>
            <Typography>
              These parameters are used to track the traffic from the ad but are
              not required.
            </Typography>
            <ul>
              <li>
                <strong>{I_MEDIUM}</strong>: Represents the medium of the
                traffic. Set this to{" "}
                <code>{metaIAIAdditionalMapping[I_MEDIUM]}</code>.
              </li>
              <li>
                <strong>{I_CAMPAIGNID}</strong>: Dynamically inserts the
                campaign ID. Use{" "}
                <code>{metaIAIAdditionalMapping[I_CAMPAIGNID]}</code>.
              </li>
              <li>
                <strong>{I_CONTENTID}</strong>: Dynamically inserts the ad set
                ID. Use <code>{metaIAIAdditionalMapping[I_CONTENTID]}</code>.
              </li>
            </ul>
          </Box>
        );
      case 5:
        return (
          <Typography>
            Click <strong>Publish</strong> when finished configuring your ad.
          </Typography>
        );
      default:
        return "Unknown step";
    }
  };

  return (
    <Container>
      <Typography variant="h4" align="center" mb={4}>
        Setting Up Incendium Tracking Parameters in Meta Ads Manager
      </Typography>
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((label, i) => (
          <Step key={label} disabled={false}>
            <StepButton onClick={() => setActiveStep(i)}>{label}</StepButton>
          </Step>
        ))}
      </Stepper>
      <Stack mt={4} spacing={4}>
        {renderStepContent(activeStep)}
        <Divider />
        <Box>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={activeStep === steps.length - 1 ? onFinish : handleNext}
          >
            {activeStep === steps.length - 1 ? "Finish" : "Next"}
          </Button>
        </Box>
      </Stack>
    </Container>
  );
}

export default MetaAdsTrackingSetup;
